import type { JizdaListItem } from '@/api/types/Jizda';

// In-day grouping for the weekly calendar (CHR-03 §6 / ADR-0051). Within a day
// column the jízdy are grouped by commodity → loading place → unloading place
// → plannedArrival, with commodities kept CONTIGUOUS (never interleaved) and a
// commodity sub-header per day. The server already returns rows ordered by
// `loadingDate, plannedArrival, id ASC`, so this is a stable group-by that
// preserves that order inside each commodity bucket.

export interface CommodityGroup {
  // Stable key for React lists — the commodity id, or a sentinel for the
  // "no commodity" bucket.
  key: string;
  // Display name for the commodity sub-header (null → caller renders the
  // "Bez komodity" fallback label).
  commodityName: string | null;
  // The jízdy in this commodity bucket, ordered by loading place → unloading
  // place → plannedArrival (server order preserved within ties).
  items: JizdaListItem[];
}

const NO_COMMODITY_KEY = '__none__';

// Compare two (possibly-null) strings for a stable sort; nulls sort last.
const cmpNullable = (a: string | null, b: string | null): number => {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a.localeCompare(b, 'cs');
};

// Group a single day's jízdy into contiguous commodity buckets, each
// internally ordered by loading place → unloading place → plannedArrival.
// Commodity buckets themselves are ordered by commodity name (nulls last) so
// the column reads deterministically day-to-day.
export const groupDayByCommodity = (items: JizdaListItem[]): CommodityGroup[] => {
  const buckets = new Map<string, CommodityGroup>();
  for (const item of items) {
    const key = item.commodity?.id ?? NO_COMMODITY_KEY;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = {
        key,
        commodityName: item.commodity?.name ?? null,
        items: [],
      };
      buckets.set(key, bucket);
    }
    bucket.items.push(item);
  }

  const groups = [...buckets.values()];
  // Order commodity buckets by name (nulls / "no commodity" last).
  groups.sort((a, b) => cmpNullable(a.commodityName, b.commodityName));
  // Order within each bucket: loading place → unloading place → plannedArrival.
  for (const group of groups) {
    group.items.sort((a, b) => {
      const byLoad = cmpNullable(a.loadingPlace, b.loadingPlace);
      if (byLoad !== 0) return byLoad;
      const byUnload = cmpNullable(a.unloadingPlace, b.unloadingPlace);
      if (byUnload !== 0) return byUnload;
      return cmpNullable(a.plannedArrival, b.plannedArrival);
    });
  }
  return groups;
};

// Index the week's items by `loadingDate` (ISO). Rows whose `loadingDate` is
// null are dropped — the bounded week-fetch already excludes them, but this is
// defensive. Returns a Map keyed by ISO date → that day's items (server order
// preserved; the per-day grouping reorders within the day).
export const indexByDay = (
  items: JizdaListItem[],
): Map<string, JizdaListItem[]> => {
  const byDay = new Map<string, JizdaListItem[]>();
  for (const item of items) {
    if (!item.loadingDate) continue;
    const list = byDay.get(item.loadingDate);
    if (list) {
      list.push(item);
    } else {
      byDay.set(item.loadingDate, [item]);
    }
  }
  return byDay;
};
